import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  makeStyles,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  FileCopy as CopyIcon,
  Send as SendIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@material-ui/icons";
import PixIcon from "@mui/icons-material/Pix";
import { toast } from "react-toastify";
import api from "../../services/api";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#00BFA5",
    color: "#fff",
    padding: theme.spacing(1, 2),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  pixIcon: {
    fontSize: 28,
  },
  content: {
    padding: theme.spacing(2),
    minWidth: 380,
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  previewBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#e8f5e9",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid #00BFA5`,
  },
  previewTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#00BFA5",
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  previewContent: {
    fontFamily: "inherit",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.95rem",
    lineHeight: 1.6,
  },
  editSection: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  editHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  editTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  keyDisplay: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#fff",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  noKeyWarning: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ff9800",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    textAlign: "center",
    color: "#e65100",
  },
  // Multi-key styles
  keyListItem: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#00BFA5",
      backgroundColor: theme.palette.type === "dark" ? "#333" : "#f0faf9",
    },
  },
  keyListItemSelected: {
    borderColor: "#00BFA5",
    borderWidth: 2,
    backgroundColor: theme.palette.type === "dark" ? "#1a332e" : "#e0f7f3",
  },
  keyLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  keyChip: {
    height: 22,
    fontSize: "0.7rem",
  },
  addKeyBox: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
  },
  addKeyTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const PIX_TYPES = [
  { value: "cpf", label: "CPF", placeholder: "000.000.000-00" },
  { value: "cnpj", label: "CNPJ", placeholder: "00.000.000/0000-00" },
  { value: "email", label: "E-mail", placeholder: "email@exemplo.com" },
  { value: "celular", label: "Celular", placeholder: "+55 11 99999-9999" },
  { value: "aleatoria", label: "Chave Aleatoria", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
];

const PixSendModal = ({ open, onClose, onSend, ticketId }) => {
  const classes = useStyles();
  const { update } = useSettings();

  // Multi-key state
  const [pixKeys, setPixKeys] = useState([]); // [{id, label, type, key, name}]
  const [selectedKeyIndex, setSelectedKeyIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for adding/editing a key
  const [editingKeyIndex, setEditingKeyIndex] = useState(-1); // -1 = adding new
  const [formLabel, setFormLabel] = useState("");
  const [formType, setFormType] = useState("");
  const [formKey, setFormKey] = useState("");
  const [formName, setFormName] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!open) return;

      setLoadingSettings(true);
      setEditMode(false);
      setSelectedKeyIndex(-1);
      setShowForm(false);
      setEditingKeyIndex(-1);
      try {
        const { data } = await api.get("/settings");

        // Try new multi-key format first
        const pixKeysConfig = data.find(s => s.key === "pixKeys");

        if (pixKeysConfig?.value) {
          try {
            const parsed = JSON.parse(pixKeysConfig.value);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setPixKeys(parsed);
              setSelectedKeyIndex(0);
              setLoadingSettings(false);
              return;
            }
          } catch (e) {
            // Invalid JSON, fall through to legacy
          }
        }

        // Fallback: migrate from legacy single-key settings
        const pixTypeConfig = data.find(s => s.key === "efipixtype");
        const pixKeyConfig = data.find(s => s.key === "efichavepix");
        const pixNameConfig = data.find(s => s.key === "efipixname");

        const type = pixTypeConfig?.value || "";
        const key = pixKeyConfig?.value || "";
        const name = pixNameConfig?.value || "";

        if (key) {
          const legacyKey = {
            id: Date.now().toString(),
            label: "Principal",
            type,
            key,
            name,
            favorite: true,
          };
          setPixKeys([legacyKey]);
          setSelectedKeyIndex(0);
        } else {
          setPixKeys([]);
          setEditMode(true);
          setShowForm(true);
        }
      } catch (err) {
        console.error("Erro ao carregar configuracoes PIX:", err);
        setEditMode(true);
        setShowForm(true);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, [open]);

  const handleCopyKey = (key) => {
    if (!key) {
      toast.info("Nenhuma chave PIX para copiar");
      return;
    }
    navigator.clipboard.writeText(key);
    toast.success("Chave PIX copiada!");
  };

  const resetForm = () => {
    setFormLabel("");
    setFormType("");
    setFormKey("");
    setFormName("");
    setEditingKeyIndex(-1);
  };

  const handleToggleFavorite = (index) => {
    const updated = pixKeys.map((pk, i) => ({
      ...pk,
      favorite: i === index ? true : false,
    }));
    setPixKeys(updated);
  };

  const handleAddKey = () => {
    resetForm();
    setShowForm(true);
    setEditingKeyIndex(-1);
  };

  const handleEditKey = (index) => {
    const key = pixKeys[index];
    setFormLabel(key.label || "");
    setFormType(key.type || "");
    setFormKey(key.key || "");
    setFormName(key.name || "");
    setEditingKeyIndex(index);
    setShowForm(true);
  };

  const handleDeleteKey = (index) => {
    const updated = pixKeys.filter((_, i) => i !== index);
    setPixKeys(updated);
    if (selectedKeyIndex === index) {
      setSelectedKeyIndex(updated.length > 0 ? 0 : -1);
    } else if (selectedKeyIndex > index) {
      setSelectedKeyIndex(selectedKeyIndex - 1);
    }
  };

  const handleSaveKeyForm = () => {
    if (!formKey) {
      toast.error("Informe a chave PIX");
      return;
    }
    if (!formLabel) {
      toast.error("Informe um rotulo (ex: Matriz, Filial 1)");
      return;
    }

    const isFirst = pixKeys.length === 0 && editingKeyIndex < 0;
    const newKey = {
      id: editingKeyIndex >= 0 ? pixKeys[editingKeyIndex].id : Date.now().toString(),
      label: formLabel,
      type: formType,
      key: formKey,
      name: formName,
      favorite: editingKeyIndex >= 0 ? (pixKeys[editingKeyIndex].favorite || false) : isFirst,
    };

    if (editingKeyIndex >= 0) {
      // Editing existing
      const updated = [...pixKeys];
      updated[editingKeyIndex] = newKey;
      setPixKeys(updated);
    } else {
      // Adding new
      setPixKeys(prev => [...prev, newKey]);
      setSelectedKeyIndex(pixKeys.length); // Select the new one
    }

    setShowForm(false);
    resetForm();
  };

  const handleSaveSettings = async () => {
    if (pixKeys.length === 0) {
      toast.error("Adicione pelo menos uma chave PIX");
      return;
    }

    setSaving(true);
    try {
      // Save in new multi-key format
      await update({ key: "pixKeys", value: JSON.stringify(pixKeys) });

      // Also update legacy settings with the first key for backward compat
      const primary = pixKeys[0];
      await update({ key: "efipixtype", value: primary.type || "" });
      await update({ key: "efichavepix", value: primary.key || "" });
      await update({ key: "efipixname", value: primary.name || "" });

      toast.success("Dados PIX salvos!");
      setEditMode(false);
      setShowForm(false);
    } catch (err) {
      toast.error("Erro ao salvar dados PIX");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (pixKeys.length === 0 || selectedKeyIndex < 0) {
      toast.error("Selecione uma chave PIX para enviar");
      return;
    }

    const selected = pixKeys[selectedKeyIndex];
    if (!selected?.key) {
      toast.error("Chave PIX invalida");
      return;
    }

    setLoading(true);
    try {
      const typeLabel = PIX_TYPES.find(t => t.value === selected.type)?.label || "PIX";

      let bodyText = `💠 *PIX*\n`;
      if (selected.label) {
        bodyText += `🏢 *${selected.label}*\n`;
      }
      if (selected.name) {
        bodyText += `👤 *${selected.name}*\n`;
      }
      bodyText += `📋 *Chave ${typeLabel}:*\n`;
      bodyText += `\`\`\`${selected.key}\`\`\``;

      await api.post(`/tickets/${ticketId}/interactive`, {
        type: "pix_copy",
        bodyText,
        copyCode: selected.key,
      });

      toast.success("Chave PIX enviada!");
      onClose();
    } catch (err) {
      toast.error("Erro ao enviar chave PIX");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedKey = () => {
    if (selectedKeyIndex >= 0 && selectedKeyIndex < pixKeys.length) {
      return pixKeys[selectedKeyIndex];
    }
    return null;
  };

  const selectedKey = getSelectedKey();
  const selectedType = selectedKey ? PIX_TYPES.find(t => t.value === selectedKey.type) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <PixIcon className={classes.pixIcon} />
          <Typography variant="h6">PIX Chat - Enviar Chave</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loadingSettings ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Key List — always visible, select which to send */}
            {pixKeys.length > 0 && !editMode && (
              <>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Selecione a chave PIX para enviar:
                </Typography>
                {pixKeys.map((pk, index) => {
                  const isSelected = selectedKeyIndex === index;
                  const typeInfo = PIX_TYPES.find(t => t.value === pk.type);
                  return (
                    <Box
                      key={pk.id}
                      className={`${classes.keyListItem} ${isSelected ? classes.keyListItemSelected : ""}`}
                      onClick={() => setSelectedKeyIndex(index)}
                      p={1.5}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box className={classes.keyLabel}>
                          {isSelected && (
                            <CheckCircleIcon style={{ color: "#00BFA5", fontSize: 20 }} />
                          )}
                          <Box>
                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                              {pk.favorite && (
                                <StarIcon style={{ color: "#FFB300", fontSize: 16 }} />
                              )}
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {pk.label || "Sem rotulo"}
                              </Typography>
                              {typeInfo && (
                                <Chip
                                  label={typeInfo.label}
                                  size="small"
                                  className={classes.keyChip}
                                  variant="outlined"
                                />
                              )}
                              {pk.favorite && (
                                <Chip
                                  label="Favorita"
                                  size="small"
                                  className={classes.keyChip}
                                  style={{ backgroundColor: "#FFF8E1", color: "#F57F17", borderColor: "#FFB300" }}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              style={{ fontFamily: "monospace", color: "#666" }}
                            >
                              {pk.key}
                            </Typography>
                            {pk.name && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Titular: {pk.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Tooltip title="Copiar chave">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyKey(pk.key);
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {pixKeys.length === 0 && !editMode && (
              <Box className={classes.noKeyWarning}>
                <Typography variant="body1" gutterBottom>
                  Nenhuma chave PIX configurada
                </Typography>
                <Typography variant="body2">
                  Clique em "Editar" para configurar suas chaves PIX
                </Typography>
              </Box>
            )}

            {/* Edit toggle button */}
            <Box
              className={classes.editHeader}
              onClick={() => {
                setEditMode(!editMode);
                if (editMode) {
                  setShowForm(false);
                  resetForm();
                }
              }}
              style={{ marginTop: 16 }}
            >
              <Box className={classes.editTitle}>
                <EditIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  {editMode ? "Ocultar edicao" : "Gerenciar chaves PIX"}
                </Typography>
              </Box>
              {editMode ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            {/* Edit Mode */}
            <Collapse in={editMode}>
              <Box className={classes.editSection}>
                {/* Existing keys list with edit/delete */}
                {pixKeys.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                      Chaves cadastradas ({pixKeys.length})
                    </Typography>
                    {pixKeys.map((pk, index) => {
                      const typeInfo = PIX_TYPES.find(t => t.value === pk.type);
                      return (
                        <Box
                          key={pk.id}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          p={1}
                          mb={1}
                          style={{
                            borderRadius: 8,
                            border: `1px solid ${editingKeyIndex === index ? "#00BFA5" : "#e0e0e0"}`,
                            backgroundColor: editingKeyIndex === index ? "#e0f7f3" : "transparent",
                          }}
                        >
                          <Box>
                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                              <BusinessIcon fontSize="small" color="action" />
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {pk.label || "Sem rotulo"}
                              </Typography>
                              {typeInfo && (
                                <Chip label={typeInfo.label} size="small" className={classes.keyChip} />
                              )}
                            </Box>
                            <Typography variant="caption" style={{ fontFamily: "monospace" }}>
                              {pk.key}
                            </Typography>
                          </Box>
                          <Box>
                            <Tooltip title={pk.favorite ? "Chave favorita (botao rapido)" : "Marcar como favorita"}>
                              <IconButton size="small" onClick={() => handleToggleFavorite(index)}>
                                {pk.favorite
                                  ? <StarIcon fontSize="small" style={{ color: "#FFB300" }} />
                                  : <StarBorderIcon fontSize="small" />
                                }
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => handleEditKey(index)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover">
                              <IconButton size="small" onClick={() => handleDeleteKey(index)}>
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                      <StarIcon style={{ fontSize: 14, color: "#FFB300", verticalAlign: "middle", marginRight: 4 }} />
                      A chave favorita sera enviada pelo botao rapido do toolbar (icone PIX no topo do chat).
                    </Typography>
                    <Divider style={{ margin: "12px 0" }} />
                  </>
                )}

                {/* Add new key button */}
                {!showForm && (
                  <Button
                    onClick={handleAddKey}
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    style={{ borderStyle: "dashed", marginBottom: 12 }}
                  >
                    Adicionar nova chave PIX
                  </Button>
                )}

                {/* Add/Edit key form */}
                <Collapse in={showForm}>
                  <Box className={classes.addKeyBox}>
                    <Typography className={classes.addKeyTitle}>
                      <PixIcon style={{ fontSize: 20, color: "#00BFA5" }} />
                      {editingKeyIndex >= 0 ? "Editar chave PIX" : "Nova chave PIX"}
                    </Typography>

                    <TextField
                      label="Rotulo / Identificacao"
                      placeholder="Ex: Matriz, Filial Centro, Loja 01..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={formLabel}
                      onChange={(e) => setFormLabel(e.target.value)}
                      className={classes.formControl}
                      helperText="Identifica qual chave PIX sera usada"
                    />

                    <FormControl variant="outlined" size="small" className={classes.formControl}>
                      <InputLabel>Tipo de Chave</InputLabel>
                      <Select
                        value={formType}
                        onChange={(e) => setFormType(e.target.value)}
                        label="Tipo de Chave"
                      >
                        <MenuItem value="">Selecione...</MenuItem>
                        {PIX_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label={
                        formType
                          ? `Chave ${PIX_TYPES.find(t => t.value === formType)?.label || "PIX"}`
                          : "Chave PIX"
                      }
                      placeholder={
                        PIX_TYPES.find(t => t.value === formType)?.placeholder || "Digite a chave PIX"
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={formKey}
                      onChange={(e) => setFormKey(e.target.value)}
                      className={classes.formControl}
                    />

                    <TextField
                      label="Nome do Titular"
                      placeholder="Nome completo do titular da conta"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className={classes.formControl}
                    />

                    <Box display="flex" style={{ gap: 8 }}>
                      <Button
                        onClick={() => {
                          setShowForm(false);
                          resetForm();
                        }}
                        color="default"
                        variant="outlined"
                        style={{ flex: 1 }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveKeyForm}
                        color="primary"
                        variant="contained"
                        style={{ flex: 1, backgroundColor: "#00BFA5", color: "#fff" }}
                        disabled={!formKey || !formLabel}
                        startIcon={editingKeyIndex >= 0 ? <SaveIcon /> : <AddIcon />}
                      >
                        {editingKeyIndex >= 0 ? "Atualizar" : "Adicionar"}
                      </Button>
                    </Box>
                  </Box>
                </Collapse>

                {/* Save all button */}
                <Button
                  onClick={handleSaveSettings}
                  color="primary"
                  variant="contained"
                  fullWidth
                  disabled={saving || pixKeys.length === 0}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  style={{
                    marginTop: 16,
                    backgroundColor: pixKeys.length > 0 ? "#00BFA5" : undefined,
                    color: pixKeys.length > 0 ? "#fff" : undefined,
                  }}
                >
                  Salvar todas as chaves
                </Button>
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!selectedKey || loading || editMode}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          style={{
            backgroundColor: selectedKey && !editMode ? "#00BFA5" : undefined,
            color: selectedKey && !editMode ? "#fff" : undefined,
          }}
        >
          Enviar para cliente
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PixSendModal;
